import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GET, POST, PUT, DELETE, loading, alert, toFixed } from "./CustomComponent";
import TaxStepper from "./personal-tax/TaxStepper";
import StepIncome from "./personal-tax/StepIncome";
import IncomeDetail from "./personal-tax/IncomeDetail";
import StepDeduction from "./personal-tax/StepDeduction";
import StepReview from "./personal-tax/StepReview";
import { DEFAULT_INCOME_TAG_COLOR, emptyIncomeRow, LAST_STEP, RD_GREEN, STEP_KEY, toThaiYear } from "./personal-tax/constants";

// ภาษีเงินได้บุคคลธรรมดาของผู้ใช้ที่ล็อกอิน จัดหน้าเป็น 4 ขั้นตอนตามแบบ ภ.ง.ด.90/91 บนเว็บ e-Filing
// เปิดมาเจอหน้าตรวจสอบข้อมูลก่อน (ข้อมูลผู้มีเงินได้ดึงจากระบบ) แล้วค่อยกรอกเงินได้ > ค่าลดหย่อน
// ทุกขั้นตอนบันทึกให้อัตโนมัติเมื่อหยุดพิมพ์ และบันทึกซ้ำอีกครั้งตอนเปลี่ยนขั้นตอน
const API = "v1/admin/personal-taxes";

// หน่วงก่อนบันทึกอัตโนมัติ กันไม่ให้ยิง API ทุกตัวอักษรที่พิมพ์
const AUTOSAVE_DELAY = 1000;

const num = (value) => Number(value) || 0;

const emptyFiling = () => ({
  taxpayer_status: "",
  spouse_name: "",
  spouse_tax_id: "",
  income_source: "domestic",
  shop_name: "",
  shop_website: "",
  refund_request: null,
  status: "draft",
  submitted_at: null,
});

// แปลงรายการที่บันทึกไว้แล้วให้อยู่ในรูปแบบฟอร์มของหน้ากรอกเงินได้
const toEditableRow = (item) => ({
  key: item.uid,
  uid: item.uid,
  amount: item.amount ?? "",
  wht_amount: item.wht_amount ?? "",
  payer_tax_id: item.payer_tax_id || "",
  payer_name: item.payer_name || "",
  income_date: item.income_date ? String(item.income_date).slice(0, 10) : "",
  expense_mode: item.expense_mode || "standard",
  expense_amount: item.expense_amount ?? "",
  note: item.note || "",
  detail: item.detail || "",
  color: item.color || DEFAULT_INCOME_TAG_COLOR,
});

// employeeUid = ดูข้อมูลภาษีของพนักงานคนอื่น (เฉพาะ CEO จากหน้าโปรไฟล์) ส่ง employee_id ไปกับเส้นอ่านทุกเส้น
// readOnly = โหมดดูอย่างเดียว แสดงเฉพาะหน้าสรุป ไม่กรอก/ไม่บันทึกอัตโนมัติ
export default function PersonalTaxPanel({ hasEmployee, employeeUid = null, readOnly = false }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [years, setYears] = useState([currentYear]);
  const [catalog, setCatalog] = useState(null);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [filing, setFiling] = useState(emptyFiling());

  const [step, setStep] = useState(STEP_KEY.review);

  // หน้ากรอกเงินได้รายประเภท (ขั้นตอนที่ 2)
  const [incomeCategory, setIncomeCategory] = useState(null);
  const [incomeRows, setIncomeRows] = useState([]);

  // ค่าลดหย่อนที่กำลังกรอกอยู่ในขั้นตอนที่ 3
  const [deductionValues, setDeductionValues] = useState({});

  // รวมเงินเดือนงวดที่ยังไม่ออกจนถึงสิ้นปีเข้าไปในการคำนวณหรือไม่
  const [includeProjection, setIncludeProjection] = useState(false);
  const [showProjectionDetail, setShowProjectionDetail] = useState(false);

  // สถานะการบันทึกอัตโนมัติที่โชว์ให้ผู้ใช้เห็นท้ายหน้า
  const [saveState, setSaveState] = useState("idle");

  const dirtyRef = useRef({ filing: false, deduction: false, income: false });
  const timerRef = useRef(null);
  // กันไม่ให้บันทึกซ้อนกัน ถ้ารอบก่อนยังไม่เสร็จ (เช่นพิมพ์ต่อระหว่างรอ API) จะเลื่อนไปทำรอบถัดไป
  const savingRef = useRef(false);
  const stateRef = useRef({});

  const loadCatalog = useCallback(async () => {
    const result = await GET(`${API}/catalog`);
    if (result?.status) setCatalog(result.data);

    const yearResult = await GET(`${API}/years`, employeeUid ? { employee_id: employeeUid } : undefined);
    if (yearResult?.status && yearResult.data?.length) setYears(yearResult.data);
  }, [employeeUid]);

  // silent = โหลดเบื้องหลังหลังบันทึกอัตโนมัติ ไม่ต้องขึ้น loading เต็มจอ
  const loadSummary = useCallback(
    async (silent = false) => {
      if (!hasEmployee) return;
      if (!silent) loading(true);
      const target = employeeUid ? { employee_id: employeeUid } : {};
      const [summaryResult, itemResult, filingResult] = await Promise.all([
        GET(`${API}/summary`, { year, projection: includeProjection ? 1 : 0, ...target }),
        GET(`${API}/items`, { year, ...target }),
        GET(`${API}/filing`, { year, ...target }),
      ]);
      if (summaryResult?.status) setSummary(summaryResult.data);
      if (itemResult?.status) {
        const rows = itemResult.data || [];
        setItems(rows);
        // ถ้าผู้ใช้กำลังพิมพ์ค่าลดหย่อนค้างอยู่ อย่าเอาค่าจากเซิร์ฟเวอร์มาทับ
        if (!dirtyRef.current.deduction) {
          setDeductionValues(
            rows
              .filter((item) => item.kind === "deduction")
              .reduce((acc, item) => ({ ...acc, [item.category]: num(acc[item.category]) + num(item.amount) }), {})
          );
        }
      }
      if (filingResult?.status && !dirtyRef.current.filing) {
        setFiling({ ...emptyFiling(), ...(filingResult.data || {}) });
      }
      if (!silent) loading(false);
    },
    [hasEmployee, year, includeProjection, employeeUid]
  );

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const incomeTypeMap = useMemo(
    () => (catalog?.income_types || []).reduce((acc, type) => ({ ...acc, [type.value]: type }), {}),
    [catalog]
  );

  // ยอดรวมรายประเภทเงินได้ ใช้แสดงในรายการขั้นตอนที่ 2
  const totalsByCategory = useMemo(() => {
    const map = {};
    (summary?.income?.rows || []).forEach((row) => {
      const rowItems = row.items || [];
      map[row.category] = {
        gross: num(row.gross),
        wht: rowItems.reduce((sum, item) => sum + num(item.wht_amount), 0),
        payroll_count: rowItems.filter((item) => item.source !== "manual").length,
      };
    });
    return map;
  }, [summary]);

  // รายการเงินได้ที่ระบบดึงมาให้เอง (สลิปเงินเดือน/ประมาณการ) แยกตามประเภท ใช้โชว์แบบอ่านอย่างเดียว
  const systemIncomeByCategory = useMemo(() => {
    const map = {};
    (summary?.income?.rows || []).forEach((row) => {
      map[row.category] = (row.items || []).filter((item) => item.source !== "manual");
    });
    return map;
  }, [summary]);

  // ลำดับรวมของรายการในประเภทที่กำลังเปิดฟอร์ม (ทั้งสลิปและกรอกเอง) ตามที่เซิร์ฟเวอร์เรียงไว้
  const incomeOrderKeys = useMemo(
    () => ((summary?.income?.rows || []).find((row) => row.category === incomeCategory)?.items || []).map((item) => item.key).filter(Boolean),
    [summary, incomeCategory]
  );

  const deductionAllowedMap = useMemo(
    () => (summary?.deductions?.rows || []).reduce((acc, row) => ({ ...acc, [row.category]: num(row.allowed) }), {}),
    [summary]
  );

  const deductionAutoMap = useMemo(() => {
    const map = {};
    (summary?.deductions?.rows || []).forEach((row) => {
      const auto = (row.items || []).filter((item) => item.source !== "manual").reduce((sum, item) => sum + num(item.amount), 0);
      if (auto > 0) map[row.category] = auto;
    });
    return map;
  }, [summary]);

  // ให้ตัวจับเวลาบันทึกอัตโนมัติอ่านค่าล่าสุดได้เสมอ ไม่ติดค่าเก่าใน closure
  stateRef.current = { filing, deductionValues, incomeRows, incomeCategory, items, year, catalog, incomeTypeMap };

  // ---- บันทึกจริงของแต่ละขั้นตอน ----

  const persistFiling = async () => {
    const { filing: current, year: taxYear } = stateRef.current;

    // แบบยื่นเหลือแค่การขอคืนเงิน ส่วนอื่นส่งค่าที่เคยเก็บไว้ตามเดิมเพื่อไม่ให้ค่าหาย
    const result = await PUT(`${API}/filing`, {
      tax_year: taxYear,
      taxpayer_status: current.taxpayer_status || null,
      spouse_name: current.spouse_name || null,
      spouse_tax_id: current.spouse_tax_id || null,
      income_source: current.income_source || "domestic",
      shop_name: current.shop_name || null,
      shop_website: current.shop_website || null,
      refund_request: current.refund_request,
    });

    if (!result?.status) {
      alert("", "warning", "บันทึกไม่สำเร็จ", result?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่");
      return false;
    }
    return true;
  };

  // ค่าลดหย่อนเก็บประเภทละ 1 รายการ ถ้าเคยมีรายการซ้ำจะยุบให้เหลือรายการเดียวตอนบันทึก
  const persistDeductions = async () => {
    const { deductionValues: values, items: itemList, year: taxYear, catalog: currentCatalog } = stateRef.current;
    const requests = [];

    (currentCatalog?.deduction_types || []).forEach((type) => {
      const existing = itemList.filter((item) => item.kind === "deduction" && item.category === type.value);
      const amount = num(values[type.value]);
      const body = { tax_year: taxYear, kind: "deduction", category: type.value, label: type.label, amount };

      if (amount > 0) {
        if (existing.length) {
          if (num(existing[0].amount) !== amount) requests.push(PUT(`${API}/items/${existing[0].uid}`, body));
          existing.slice(1).forEach((item) => requests.push(DELETE(`${API}/items/${item.uid}`, null)));
        } else {
          requests.push(POST(`${API}/items`, body));
        }
      } else {
        existing.forEach((item) => requests.push(DELETE(`${API}/items/${item.uid}`, null)));
      }
    });

    if (!requests.length) return true;

    const results = await Promise.all(requests);
    const failed = results.find((result) => !result?.status);
    if (failed) {
      alert("", "warning", "บันทึกไม่สำเร็จ", failed?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่");
      return false;
    }
    return true;
  };

  // บันทึกเงินได้ทั้งประเภท: แถวใหม่ = สร้าง, แถวเดิม = แก้ไข, แถวที่ลบหรือว่างทั้งใบ = ลบทิ้ง
  // แถวที่เพิ่งสร้างจะเก็บ uid กลับมาใส่ในฟอร์ม รอบบันทึกถัดไปจะได้ไม่สร้างซ้ำ
  const persistIncome = async () => {
    const { incomeCategory: category, incomeRows: rows, items: itemList, year: taxYear, incomeTypeMap: typeMap } = stateRef.current;
    const type = typeMap[category];
    if (!category || !type) return true;

    const invalid = rows.find((row) => row.expense_mode === "actual" && num(row.expense_amount) > num(row.amount));
    if (invalid) {
      alert("", "warning", "กรอกข้อมูลไม่ถูกต้อง", "ค่าใช้จ่ายตามจริงต้องไม่เกินจำนวนเงินได้");
      return false;
    }

    // sort_order = ตำแหน่งในฟอร์ม ลากสลับในฟอร์มแล้วลำดับในตารางตรวจสอบข้อมูลจะตามไปด้วย
    const buildBody = (row) => ({
      tax_year: taxYear,
      sort_order: rows.findIndex((item) => item.key === row.key) + 1,
      kind: "income",
      category,
      label: row.payer_name || type.label,
      // ช่องข้อความส่งค่าว่างไปด้วย (ไม่ใช่ undefined) ไม่งั้นล้างค่าเดิมไม่ได้ เพราะ backend จะคงค่าเดิมเมื่อไม่ส่ง field มา
      payer_name: row.payer_name || "",
      payer_tax_id: row.payer_tax_id ? String(row.payer_tax_id).replace(/[^0-9]/g, "") : "",
      income_date: row.income_date || "",
      amount: num(row.amount),
      wht_amount: num(row.wht_amount),
      expense_mode: row.expense_mode || "standard",
      expense_amount: row.expense_mode === "actual" ? num(row.expense_amount) : 0,
      note: row.note || "",
      detail: row.detail || "",
      color: row.color || DEFAULT_INCOME_TAG_COLOR,
    });

    // บันทึกทุกแถวที่มีข้อมูลอะไรก็ได้ แม้เงินได้ยังว่าง ข้ามเฉพาะแถวที่ว่างทั้งใบ (แถวเดิมที่ถูกล้างจนว่างจะถูกลบ)
    const hasContent = (row) =>
      ["amount", "wht_amount", "payer_name", "payer_tax_id", "income_date", "expense_amount", "detail", "note"].some(
        (field) => String(row[field] ?? "").trim() !== ""
      );
    const filled = rows.filter(hasContent);
    const keepUids = filled.filter((row) => row.uid).map((row) => row.uid);
    const removedUids = itemList
      .filter((item) => item.kind === "income" && item.category === category && !keepUids.includes(item.uid))
      .map((item) => item.uid);

    const [saveResults, deleteResults] = await Promise.all([
      Promise.all(
        filled.map(async (row) => {
          const result = row.uid ? await PUT(`${API}/items/${row.uid}`, buildBody(row)) : await POST(`${API}/items`, buildBody(row));
          return { key: row.key, uid: result?.data?.uid || row.uid, result };
        })
      ),
      Promise.all(removedUids.map((uid) => DELETE(`${API}/items/${uid}`, null))),
    ]);

    const failed = [...saveResults.map((row) => row.result), ...deleteResults].find((result) => !result?.status);
    if (failed) {
      alert("", "warning", "บันทึกไม่สำเร็จ", failed?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่");
      return false;
    }

    // ผูก uid ที่เพิ่งได้กลับเข้าแถวเดิม เฉพาะตอนที่ยังอยู่หน้าประเภทเดิม
    const uidByKey = saveResults.reduce((acc, row) => ({ ...acc, [row.key]: row.uid }), {});
    setIncomeRows((prev) =>
      stateRef.current.incomeCategory === category
        ? prev.map((row) => (uidByKey[row.key] && !row.uid ? { ...row, uid: uidByKey[row.key] } : row))
        : prev
    );
    return true;
  };

  // ---- ตัวคุมการบันทึกอัตโนมัติ ----

  const flushAutoSave = useCallback(async () => {
    // โหมดดูอย่างเดียวไม่มีอะไรให้บันทึก
    if (readOnly) return true;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const pending = dirtyRef.current;
    if (!pending.filing && !pending.deduction && !pending.income) return true;

    if (savingRef.current) {
      // รอบก่อนยังไม่เสร็จ ยังไม่รู้ uid ของรายการใหม่ ถ้ายิงตอนนี้จะสร้างซ้ำ
      timerRef.current = setTimeout(() => flushAutoSaveRef.current(), AUTOSAVE_DELAY);
      return true;
    }

    savingRef.current = true;
    setSaveState("saving");
    let ok = true;

    if (pending.income) {
      ok = (await persistIncome()) && ok;
      dirtyRef.current.income = false;
    }
    if (pending.deduction) {
      ok = (await persistDeductions()) && ok;
      dirtyRef.current.deduction = false;
    }
    if (pending.filing) {
      ok = (await persistFiling()) && ok;
      dirtyRef.current.filing = false;
    }

    await loadSummary(true);
    savingRef.current = false;
    setSaveState(ok ? "saved" : "idle");
    return ok;
    // persist* อ่านค่าจาก stateRef จึงไม่ต้องผูกกับ state ตรง ๆ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadSummary, readOnly]);

  const flushAutoSaveRef = useRef(flushAutoSave);
  flushAutoSaveRef.current = flushAutoSave;

  const queueAutoSave = (kind) => {
    if (readOnly) return;
    dirtyRef.current[kind] = true;
    setSaveState("saving");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      flushAutoSave();
    }, AUTOSAVE_DELAY);
  };

  // ปิดหน้าไปตอนยังพิมพ์ค้าง ให้ยิงบันทึกที่ค้างอยู่ก่อน
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const setFilingField = (field, value) => {
    setFiling((prev) => ({ ...prev, [field]: value }));
    queueAutoSave("filing");
  };

  const setDeductionValue = (category, value) => {
    setDeductionValues((prev) => ({ ...prev, [category]: value }));
    queueAutoSave("deduction");
  };

  const updateIncomeRows = (updater) => {
    setIncomeRows(updater);
    queueAutoSave("income");
  };

  const goStep = async (next) => {
    await flushAutoSave();
    setStep(next);
    setIncomeCategory(null);
  };

  const openIncomeCategory = (category) => {
    const rows = items.filter((item) => item.kind === "income" && item.category === category).map(toEditableRow);
    setIncomeRows(rows.length ? rows : [emptyIncomeRow()]);
    setIncomeCategory(category);
  };

  const closeIncomeCategory = async () => {
    await flushAutoSave();
    setIncomeCategory(null);
  };

  // ลากจัดลำดับรายการเงินได้ในฟอร์มตัวแก้ไข ได้ทุกรายการรวมรายการจากสลิป (key = uid หรือ payroll:/projection:)
  // อัปเดต summary ทันทีให้ตารางตรวจสอบข้อมูลตรงกับที่ลาก แล้วค่อยบันทึก ถ้าบันทึกไม่ผ่านจะโหลดลำดับจริงกลับมาทับ
  const reorderIncome = async (category, keys) => {
    setSummary((prev) => {
      if (!prev?.income?.rows) return prev;
      const rows = prev.income.rows.map((row) => {
        if (row.category !== category) return row;
        const rank = (item) => {
          const index = item.key ? keys.indexOf(item.key) : -1;
          return index < 0 ? Number.MAX_SAFE_INTEGER : index;
        };
        const items = (row.items || [])
          .map((item, index) => ({ item, index, rank: rank(item) }))
          .sort((a, b) => a.rank - b.rank || a.index - b.index)
          .map((entry) => entry.item);
        return { ...row, items };
      });
      return { ...prev, income: { ...prev.income, rows } };
    });

    setSaveState("saving");

    // ลากจากในฟอร์ม: รายการที่เพิ่งเพิ่มยังไม่มี uid ต้องบันทึกให้เสร็จก่อน จะได้เอา uid ไปใส่ในลำดับได้
    if (stateRef.current.incomeCategory === category) {
      await new Promise((resolve) => setTimeout(resolve, 0)); // รอให้ลำดับใหม่ในฟอร์ม commit ลง state ก่อน
      await flushAutoSave();
    }
    const rowsNow = stateRef.current.incomeRows;
    const resolved = keys.map((key) => rowsNow.find((row) => row.key === key)?.uid || key).filter((key) => !String(key).startsWith("new-"));

    const result = await PUT(`${API}/items/reorder`, { tax_year: stateRef.current.year, category, keys: resolved });
    if (!result?.status) alert("", "warning", "จัดลำดับไม่สำเร็จ", result?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่");
    await loadSummary(true);
    setSaveState(result?.status ? "saved" : "idle");
  };

  const handleNext = async () => {
    await goStep(Math.min(LAST_STEP, step + 1));
  };

  if (!hasEmployee) {
    return <div className="text-center text-secondary py-5">ต้องผูกบัญชีกับข้อมูลพนักงานก่อน จึงจะคำนวณภาษีส่วนบุคคลได้</div>;
  }

  const projection = summary?.projection || null;
  const balance = summary?.balance || 0;

  // ดูของคนอื่น: แสดงเฉพาะหน้าสรุป ไม่มีขั้นตอนกรอกและไม่บันทึก
  if (readOnly) {
    return (
      <>
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-3">
          <div>
            <h5 className="fw-semibold mb-1">
              สรุปภาษีเงินได้บุคคลธรรมดา ภ.ง.ด.90/91 ปีภาษี {toThaiYear(year)}
            </h5>
            <div className="small text-secondary">
              <i className="fa-solid fa-eye me-1" />
              โหมดดูอย่างเดียว · ข้อมูลจากสลิปที่อนุมัติแล้ว {summary?.payroll_months || 0} งวด และรายการที่พนักงานกรอกเอง
            </div>
          </div>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            {projection?.available ? (
              <div className="form-check form-switch mb-0 d-flex align-items-center gap-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="ptn-projection-switch-readonly"
                  style={{ width: 42, height: 22, cursor: "pointer" }}
                  checked={includeProjection}
                  onChange={(event) => setIncludeProjection(event.target.checked)}
                />
                <label className="form-check-label small fw-semibold mb-0" htmlFor="ptn-projection-switch-readonly" style={{ color: "#0b4f84", cursor: "pointer" }}>
                  รวมเงินเดือนอีก {projection.months?.length || 0} งวดที่จะได้จนสิ้นปี
                </label>
              </div>
            ) : null}
            <select className="form-select" style={{ width: 160, borderRadius: 12 }} value={year} onChange={(event) => setYear(Number(event.target.value))}>
              {years.map((option) => (
                <option key={option} value={option}>ปีภาษี {toThaiYear(option)}</option>
              ))}
            </select>
          </div>
        </div>

        <StepReview
          summary={summary}
          taxpayer={summary?.taxpayer}
          year={toThaiYear(year)}
          projection={projection}
          includeProjection={includeProjection}
          showProjectionDetail={showProjectionDetail}
          setShowProjectionDetail={setShowProjectionDetail}
          refundRequest={filing.refund_request}
          setRefundRequest={() => {}}
        />

        <div className="small text-secondary mt-3">
          ตัวเลขนี้เป็นการประมาณการจากข้อมูลในระบบและรายการที่พนักงานกรอกไว้ แก้ไขได้เฉพาะเจ้าของข้อมูลเท่านั้น
        </div>
      </>
    );
  }

  return (
    <>
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-3">
        <div>
          <h5 className="fw-semibold mb-1">
            แบบแสดงรายการภาษีเงินได้บุคคลธรรมดา ภ.ง.ด.90/91 ปีภาษี {toThaiYear(year)}
          </h5>
          <div className="small text-secondary">
            เงินเดือน ภาษีหัก ณ ที่จ่าย และประกันสังคม ดึงจากสลิปที่อนุมัติแล้ว {summary?.payroll_months || 0} งวด · เพิ่มงานนอกและค่าลดหย่อนเองได้
          </div>
        </div>
        <div className="d-flex align-items-center gap-3 flex-wrap">
          {projection?.available ? (
            <div className="form-check form-switch mb-0 d-flex align-items-center gap-2">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="ptn-projection-switch"
                style={{ width: 42, height: 22, cursor: "pointer" }}
                checked={includeProjection}
                onChange={(event) => setIncludeProjection(event.target.checked)}
              />
              <label className="form-check-label small fw-semibold mb-0" htmlFor="ptn-projection-switch" style={{ color: "#0b4f84", cursor: "pointer" }}>
                รวมเงินเดือนอีก {projection.months?.length || 0} งวดที่จะได้จนสิ้นปี
              </label>
            </div>
          ) : null}
          <select className="form-select" style={{ width: 160, borderRadius: 12 }} value={year} onChange={(event) => setYear(Number(event.target.value))}>
            {years.map((option) => (
              <option key={option} value={option}>ปีภาษี {toThaiYear(option)}</option>
            ))}
          </select>
        </div>
      </div>

      <TaxStepper step={step} onSelect={goStep} />

      {step === STEP_KEY.income && !incomeCategory && (
        <StepIncome catalog={catalog} totalsByCategory={totalsByCategory} onOpen={openIncomeCategory} />
      )}

      {step === STEP_KEY.income && incomeCategory && (
        <IncomeDetail
          type={incomeTypeMap[incomeCategory]}
          rows={incomeRows}
          setRows={updateIncomeRows}
          payrollRows={systemIncomeByCategory[incomeCategory] || []}
          orderKeys={incomeOrderKeys}
          onReorder={(keys) => reorderIncome(incomeCategory, keys)}
          onBack={closeIncomeCategory}
        />
      )}

      {step === STEP_KEY.deduction && (
        <StepDeduction
          catalog={catalog}
          values={deductionValues}
          setValue={setDeductionValue}
          autoMap={deductionAutoMap}
          allowedMap={deductionAllowedMap}
          personalAllowance={catalog?.personal_allowance || 0}
        />
      )}

      {step === STEP_KEY.review && (
        <StepReview
          summary={summary}
          taxpayer={summary?.taxpayer}
          year={toThaiYear(year)}
          projection={projection}
          includeProjection={includeProjection}
          showProjectionDetail={showProjectionDetail}
          setShowProjectionDetail={setShowProjectionDetail}
          refundRequest={filing.refund_request}
          setRefundRequest={(value) => setFilingField("refund_request", value)}
        />
      )}

      <div className="d-flex justify-content-between align-items-center gap-2 mt-4 pt-3 border-top">
        <button
          type="button"
          className="btn btn-outline-secondary rounded-3 px-4"
          disabled={step === 1}
          onClick={() => goStep(Math.max(1, step - 1))}
        >
          <i className="fa-solid fa-arrow-left me-2" />{step === STEP_KEY.review + 1 ? "กลับไปหน้าสรุป" : "ย้อนกลับ"}
        </button>

        <div className="d-flex align-items-center gap-3">
          <span className="small text-secondary">
            {saveState === "saving" ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin me-1" />กำลังบันทึก
              </>
            ) : saveState === "saved" ? (
              <>
                <i className="fa-solid fa-circle-check me-1" style={{ color: RD_GREEN }} />บันทึกอัตโนมัติแล้ว
              </>
            ) : null}
          </span>

          {step === STEP_KEY.review ? (
            <span className="small text-secondary">
              {balance >= 0 ? "ต้องชำระเพิ่ม" : "ชำระไว้เกิน"} {toFixed(Math.abs(balance))} บาท
            </span>
          ) : null}

          {step < LAST_STEP ? (
            <button type="button" className="btn rounded-3 px-4 text-white" style={{ background: RD_GREEN }} onClick={handleNext}>
              {step === STEP_KEY.review ? "เริ่มกรอกข้อมูล" : "ถัดไป"} <i className="fa-solid fa-arrow-right ms-2" />
            </button>
          ) : null}
        </div>
      </div>

      <div className="small text-secondary mt-3">
        ตัวเลขนี้เป็นการประมาณการเพื่อวางแผนภาษี อ้างอิงอัตราภาษีและสิทธิลดหย่อนมาตรฐาน ก่อนยื่นจริงให้ตรวจสอบสิทธิของตัวเองกับกรมสรรพากรอีกครั้ง
      </div>
    </>
  );
}
